import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LanguageProvider } from "./context/LanguageContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import StudyHub from "./pages/StudyHub";
import ChapterPractice from "./pages/ChapterPractice";
import Simulations from "./pages/Simulations";
import MockTests from "./pages/MockTests";
import RealTestUI from "./pages/RealTestUI";
import JourneyTracker from "./pages/JourneyTracker";
import Analytics from "./pages/Analytics";
import NotesPage from "./pages/NotesPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Flashcards from "./pages/Flashcards";
import WeakAreas from "./pages/WeakAreas";
import DiscoverCanada from "./pages/DiscoverCanada";
import ContactUs from "./pages/ContactUs";
import AccountSettings from "./pages/AccountSettings";
import AdminDashboard from "./pages/AdminDashboard";
import MyProgress from "./pages/MyProgress";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclaimer from "./pages/Disclaimer";
import NotFound from "./pages/NotFound";

// SEO Feature Pages
import ChapterPracticePage from "./pages/features/ChapterPracticePage";
import SimulationsPage from "./pages/features/SimulationsPage";
import MockTestsPage from "./pages/features/MockTestsPage";
import FlashcardsPage from "./pages/features/FlashcardsPage";
import AnalyticsFeaturePage from "./pages/features/AnalyticsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper to safely access localStorage (iOS Safari private mode fallback)
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage not available:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage not available:', e);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }
};

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeLocalStorage.getItem("token"));
  const hasInitialized = useRef(false);

  const checkAuth = async () => {
    const storedToken = safeLocalStorage.getItem("token") || token;
    
    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    const storedUser = safeLocalStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {}
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      safeLocalStorage.setItem("user", JSON.stringify(response.data));
    } catch (e) {
      setUser(null);
      safeLocalStorage.removeItem("token");
      safeLocalStorage.removeItem("user");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    safeLocalStorage.setItem("token", response.data.token);
    safeLocalStorage.setItem("user", JSON.stringify(response.data.user));
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, language = "en") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, language });
    safeLocalStorage.setItem("token", response.data.token);
    safeLocalStorage.setItem("user", JSON.stringify(response.data.user));
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    safeLocalStorage.removeItem("token");
    safeLocalStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      checkAuth();
    }
  }, []);

  return { user, setUser, loading, login, register, logout, token, checkAuth };
};

// Google OAuth Callback Component
const GoogleAuthCallback = () => {
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("processing");
  const [errorMsg, setErrorMsg] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const code = searchParams.get("code");
      const error = searchParams.get("error");
      
      if (error) {
        setStatus("error");
        setErrorMsg("Google sign-in was cancelled");
        setTimeout(() => { window.location.href = "/login"; }, 2000);
        return;
      }
      
      if (code) {
        try {
          const redirectUri = window.location.origin + '/auth/callback';
          const response = await axios.post(
            `${API}/auth/google`,
            { code: code, redirect_uri: redirectUri },
            { withCredentials: true, timeout: 15000 }
          );
          
          safeLocalStorage.setItem("token", response.data.token);
          safeLocalStorage.setItem("user", JSON.stringify(response.data.user));
          
          setStatus("success");
          setTimeout(() => { window.location.href = "/dashboard"; }, 100);
          
        } catch (error) {
          console.error("Auth error:", error);
          setStatus("error");
          setErrorMsg(error.response?.data?.detail || "Authentication failed");
          safeLocalStorage.removeItem("token");
          safeLocalStorage.removeItem("user");
          setTimeout(() => { window.location.href = "/login"; }, 2000);
        }
      } else {
        setStatus("error");
        setErrorMsg("No authentication data received");
        setTimeout(() => { window.location.href = "/login"; }, 1500);
      }
    };

    processAuth();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center p-8">
        {status === "processing" && (
          <>
            <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Completing sign in...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-600">Welcome to CanadaTestPrep!</p>
            <p className="text-slate-400 text-sm mt-1">Redirecting to dashboard...</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium">{errorMsg || "Authentication failed"}</p>
            <p className="text-slate-400 text-sm mt-1">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, auth }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState("checking");

  useEffect(() => {
    let isMounted = true;

    const verifyAuth = async () => {
      if (location.state?.user) {
        auth.setUser(location.state.user);
        if (isMounted) setAuthState("authenticated");
        return;
      }

      let storedToken = auth.token;
      try {
        storedToken = localStorage.getItem("token") || auth.token;
      } catch (e) {
        console.warn("localStorage not available");
      }
      
      if (!storedToken) {
        if (isMounted) setAuthState("unauthenticated");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000
        });
        if (isMounted) {
          auth.setUser(response.data);
          setAuthState("authenticated");
        }
      } catch (error) {
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        } catch (e) {}
        if (isMounted) setAuthState("unauthenticated");
      }
    };

    verifyAuth();

    return () => { isMounted = false; };
  }, [location.state, auth.token]);

  useEffect(() => {
    if (authState === "unauthenticated") {
      window.location.href = "/login";
    }
  }, [authState]);

  if (authState === "checking" || authState === "unauthenticated") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
};

// App Router
function AppRouter({ auth }) {
  return (
    <Routes>
      <Route path="/" element={<LandingPage auth={auth} />} />
      <Route path="/login" element={<LoginPage auth={auth} />} />
      <Route path="/register" element={<RegisterPage auth={auth} />} />
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />
      <Route path="/pricing" element={<PricingPage auth={auth} />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      {/* SEO Feature Pages */}
      <Route path="/features/chapter-practice" element={<ChapterPracticePage />} />
      <Route path="/features/simulations" element={<SimulationsPage />} />
      <Route path="/features/mock-tests" element={<MockTestsPage />} />
      <Route path="/features/flashcards" element={<FlashcardsPage />} />
      <Route path="/features/analytics" element={<AnalyticsFeaturePage />} />
      <Route
        path="/dashboard"
        element={<ProtectedRoute auth={auth}><Dashboard auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/discover-canada"
        element={<ProtectedRoute auth={auth}><DiscoverCanada auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/study"
        element={<ProtectedRoute auth={auth}><StudyHub auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/chapter-practice"
        element={<ProtectedRoute auth={auth}><ChapterPractice auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/simulations"
        element={<ProtectedRoute auth={auth}><Simulations auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/mock-tests"
        element={<ProtectedRoute auth={auth}><MockTests auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/test/:testId"
        element={<ProtectedRoute auth={auth}><RealTestUI auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/journey"
        element={<ProtectedRoute auth={auth}><JourneyTracker auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/analytics"
        element={<ProtectedRoute auth={auth}><Analytics auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/my-progress"
        element={<ProtectedRoute auth={auth}><MyProgress auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/weak-areas"
        element={<ProtectedRoute auth={auth}><WeakAreas auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/notes"
        element={<ProtectedRoute auth={auth}><NotesPage auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/flashcards"
        element={<ProtectedRoute auth={auth}><Flashcards auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/subscription/success"
        element={<ProtectedRoute auth={auth}><SubscriptionSuccess auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/contact"
        element={<ProtectedRoute auth={auth}><ContactUs auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/settings"
        element={<ProtectedRoute auth={auth}><AccountSettings auth={auth} /></ProtectedRoute>}
      />
      <Route
        path="/admin"
        element={<ProtectedRoute auth={auth}><AdminDashboard auth={auth} /></ProtectedRoute>}
      />
      {/* Redirect legacy routes */}
      <Route path="/study-planner" element={<Navigate to="/journey" replace />} />
      <Route path="/practice" element={<Navigate to="/chapter-practice" replace />} />
      {/* 404 catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  const auth = useAuth();

  return (
    <LanguageProvider userLanguage={auth.user?.language || 'en'}>
      <div className="App min-h-screen bg-background font-body">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter auth={auth} />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;

